import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Badge, Card, Checkbox, Input, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import type { Priority, PriorityMinutes, SlaPolicyItem } from '@/lib/types';

const onError = (e: unknown) => toast.error(errorMessage(e));
const PRIORITIES: { key: Priority; label: string }[] = [
  { key: 'URGENT', label: 'Dringend' },
  { key: 'HIGH', label: 'Hoch' },
  { key: 'NORMAL', label: 'Normal' },
  { key: 'LOW', label: 'Niedrig' },
];
const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function minutesText(m: number | null | undefined) {
  if (!m) return '–';
  if (m % 1440 === 0) return `${m / 1440} T.`;
  if (m % 60 === 0) return `${m / 60} Std.`;
  return m >= 60 ? `${Math.floor(m / 60)} Std. ${m % 60} Min.` : `${m} Min.`;
}

/** Eingabe in Stunden (Dezimal erlaubt) ↔ Minuten. */
const toHours = (m: number | null | undefined) => (m ? String(Math.round((m / 60) * 100) / 100).replace('.', ',') : '');
const toMinutes = (h: string) => {
  const n = Number(h.replace(',', '.'));
  return h.trim() && n > 0 ? Math.round(n * 60) : null;
};

interface Form {
  name: string;
  description: string;
  isDefault: boolean;
  allDay: boolean;
  days: number[];
  from: string;
  to: string;
  holidays: string;
  response: Record<Priority, string>;
  resolution: Record<Priority, string>;
  pauseWhenWaiting: boolean;
}

const hoursOf = (m: PriorityMinutes) => Object.fromEntries(PRIORITIES.map((p) => [p.key, toHours(m[p.key])])) as Record<Priority, string>;

const empty: Form = {
  name: '',
  description: '',
  isDefault: false,
  allDay: false,
  days: [1, 2, 3, 4, 5],
  from: '08:00',
  to: '17:00',
  holidays: '',
  response: { URGENT: '1', HIGH: '2', NORMAL: '4', LOW: '8' },
  resolution: { URGENT: '4', HIGH: '8', NORMAL: '24', LOW: '40' },
  pauseWhenWaiting: true,
};

function fromPolicy(p: SlaPolicyItem): Form {
  return {
    name: p.name,
    description: p.description ?? '',
    isDefault: p.isDefault,
    allDay: p.allDay,
    days: [...new Set(p.businessHours.map((h) => h.day))],
    from: p.businessHours[0]?.from ?? '08:00',
    to: p.businessHours[0]?.to ?? '17:00',
    holidays: p.holidays.join('\n'),
    response: hoursOf(p.responseMinutes),
    resolution: hoursOf(p.resolutionMinutes),
    pauseWhenWaiting: p.pauseWhenWaiting,
  };
}

export function SlaTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['sla-policies'], queryFn: () => api<SlaPolicyItem[]>('/sla-policies') });
  const [edit, setEdit] = useState<{ id: string | null; form: Form } | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [confirmDialog, confirm] = useConfirm();
  useEffect(() => {
    if (edit) setForm(edit.form);
  }, [edit]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const done = (list: SlaPolicyItem[]) => {
    qc.setQueryData(['sla-policies'], list);
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        description: form.description || null,
        isDefault: form.isDefault,
        allDay: form.allDay,
        businessHours: form.allDay ? [] : form.days.sort().map((day) => ({ day, from: form.from, to: form.to })),
        holidays: form.holidays.split(/[\s,;]+/).map((h) => h.trim()).filter(Boolean),
        responseMinutes: Object.fromEntries(PRIORITIES.map((p) => [p.key, toMinutes(form.response[p.key])])),
        resolutionMinutes: Object.fromEntries(PRIORITIES.map((p) => [p.key, toMinutes(form.resolution[p.key])])),
        pauseWhenWaiting: form.pauseWhenWaiting,
      };
      return edit!.id ? api<SlaPolicyItem[]>(`/sla-policies/${edit!.id}`, { method: 'PUT', body }) : api<SlaPolicyItem[]>('/sla-policies', { method: 'POST', body });
    },
    onSuccess: done,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<SlaPolicyItem[]>(`/sla-policies/${id}`, { method: 'DELETE' }), onSuccess: done, onError });

  return (
    <Card>
      {confirmDialog}
      <div className="flex items-center justify-between gap-3 border-b p-3">
        <p className="text-sm text-muted-foreground">
          Reaktions- und Lösungszeiten je Priorität innerhalb der Servicezeiten. Tickets erhalten die Richtlinie ihres Wartungsvertrags, sonst die Standardrichtlinie.
        </p>
        <Button size="sm" onClick={() => setEdit({ id: null, form: { ...empty, isDefault: (data?.length ?? 0) === 0 } })}>
          <Plus /> Richtlinie
        </Button>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Richtlinie</TH>
            <TH className="hidden md:table-cell">Servicezeiten</TH>
            {PRIORITIES.map((p) => (
              <TH key={p.key} className="hidden text-center lg:table-cell">
                {p.label}
              </TH>
            ))}
            <TH className="w-24" />
          </TR>
        </THead>
        <TBody>
          {data?.length === 0 && <EmptyRow colSpan={7}>Noch keine SLA-Richtlinie – ohne Richtlinie haben Tickets keine Fristen.</EmptyRow>}
          {data?.map((p) => (
            <TR key={p.id}>
              <TD>
                <p className="flex items-center gap-2 font-medium">
                  {p.name} {p.isDefault && <Badge><Star className="size-3" /> Standard</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {p.contractCount} Verträge{p.pauseWhenWaiting ? ' · hält bei „Warten“ an' : ''}
                </p>
              </TD>
              <TD className="hidden text-sm md:table-cell">
                {p.allDay ? '24/7' : p.businessHours.length ? `${p.businessHours.map((h) => DAYS[h.day - 1]).join(', ')} ${p.businessHours[0]!.from}–${p.businessHours[0]!.to}` : '–'}
              </TD>
              {PRIORITIES.map((pr) => (
                <TD key={pr.key} className="hidden text-center text-xs lg:table-cell">
                  <span className="block">{minutesText(p.responseMinutes[pr.key])}</span>
                  <span className="block text-muted-foreground">{minutesText(p.resolutionMinutes[pr.key])}</span>
                </TD>
              ))}
              <TD className="text-right">
                <Button variant="ghost" size="icon" className="size-8" aria-label={`${p.name} bearbeiten`} onClick={() => setEdit({ id: p.id, form: fromPolicy(p) })}>
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  aria-label={`${p.name} löschen`}
                  onClick={async () =>
                    (await confirm({ title: `Richtlinie „${p.name}“ löschen?`, description: 'Verträge mit dieser Richtlinie verwenden danach die Standardrichtlinie.', confirmLabel: 'Löschen', destructive: true })) &&
                    del.mutate(p.id)
                  }
                >
                  <Trash2 />
                </Button>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {data && data.length > 0 && <p className="border-t p-3 text-xs text-muted-foreground">Je Priorität: oben Reaktionszeit, unten Lösungszeit.</p>}

      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent wide>
          <form
            className="flex min-h-0 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>{edit?.id ? 'SLA-Richtlinie bearbeiten' : 'Neue SLA-Richtlinie'}</DialogTitle>
              <DialogDescription>Zeiten in Stunden innerhalb der Servicezeiten (z. B. 0,5 = 30 Minuten). Leer = keine Vorgabe.</DialogDescription>
            </DialogHeader>
            <DialogBody className="grid gap-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" htmlFor="s-name">
                  <Input id="s-name" required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="z. B. Premium" />
                </Field>
                <Field label="Beschreibung" htmlFor="s-desc">
                  <Input id="s-desc" value={form.description} onChange={(e) => set('description', e.target.value)} />
                </Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
                  <Switch checked={form.isDefault} onCheckedChange={(v) => set('isDefault', v)} /> Standard für Tickets ohne Vertrag
                </label>
                <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
                  <Switch checked={form.allDay} onCheckedChange={(v) => set('allDay', v)} /> Rund um die Uhr (24/7)
                </label>
                <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
                  <Switch checked={form.pauseWhenWaiting} onCheckedChange={(v) => set('pauseWhenWaiting', v)} /> Bei „Warten auf Kunde“ anhalten
                </label>
              </div>
              {!form.allDay && (
                <fieldset className="grid gap-3 rounded-lg border p-4">
                  <legend className="px-1 text-sm font-medium">Servicezeiten (Zeitzone des Unternehmens)</legend>
                  <div className="flex flex-wrap gap-3">
                    {DAYS.map((d, i) => (
                      <label key={d} className="flex items-center gap-1.5 text-sm">
                        <Checkbox checked={form.days.includes(i + 1)} onCheckedChange={() => set('days', form.days.includes(i + 1) ? form.days.filter((x) => x !== i + 1) : [...form.days, i + 1])} />
                        {d}
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Input type="time" className="w-32" value={form.from} onChange={(e) => set('from', e.target.value)} aria-label="Beginn" /> bis
                    <Input type="time" className="w-32" value={form.to} onChange={(e) => set('to', e.target.value)} aria-label="Ende" /> Uhr
                  </div>
                  <Field label="Feiertage ohne Servicezeit (JJJJ-MM-TT, eine pro Zeile)" htmlFor="s-hol">
                    <Textarea id="s-hol" rows={3} value={form.holidays} onChange={(e) => set('holidays', e.target.value)} placeholder={'2026-12-24\n2026-12-25'} />
                  </Field>
                </fieldset>
              )}
              <div className="overflow-hidden rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Priorität</th>
                      <th className="px-3 py-2 text-left font-medium">Reaktionszeit (Std.)</th>
                      <th className="px-3 py-2 text-left font-medium">Lösungszeit (Std.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PRIORITIES.map((p) => (
                      <tr key={p.key} className="border-t">
                        <td className="px-3 py-2 font-medium">{p.label}</td>
                        <td className="px-3 py-1.5">
                          <Input inputMode="decimal" value={form.response[p.key]} onChange={(e) => set('response', { ...form.response, [p.key]: e.target.value })} aria-label={`Reaktionszeit ${p.label}`} />
                        </td>
                        <td className="px-3 py-1.5">
                          <Input inputMode="decimal" value={form.resolution[p.key]} onChange={(e) => set('resolution', { ...form.resolution, [p.key]: e.target.value })} aria-label={`Lösungszeit ${p.label}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEdit(null)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!form.name || (!form.allDay && form.days.length === 0)}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
